import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

export const THEME_PRESETS = {
  blue:    { name: "Professional Blue",  header_color: "#1E3A8A", accent_color: "#2563EB", sidebar_color: "#FFFFFF", header_text_color: "#FFFFFF" },
  orange:  { name: "Orange",  header_color: "#EA580C", accent_color: "#FB923C", sidebar_color: "#FFFFFF", header_text_color: "#FFFFFF" },
  gray:    { name: "Concrete Gray",      header_color: "#374151", accent_color: "#6B7280", sidebar_color: "#FFFFFF", header_text_color: "#FFFFFF" },
  green:   { name: "Forest Green",       header_color: "#166534", accent_color: "#22C55E", sidebar_color: "#FFFFFF", header_text_color: "#FFFFFF" },
  black:   { name: "Black and Gold",     header_color: "#111827", accent_color: "#FBBF24", sidebar_color: "#111827", header_text_color: "#FBBF24" },
};

const defaultTheme = { theme_preset: "blue", ...THEME_PRESETS.blue, logo_url: "" };

const ThemeContext = createContext({ theme: defaultTheme, setTheme: () => {}, applyPreview: () => {} });

// Convert #RRGGBB to rgb() and darken/lighten
const shade = (hex, pct) => {
  const c = hex.replace("#", "");
  const num = parseInt(c, 16);
  let r = (num >> 16) + Math.round(255 * pct);
  let g = ((num >> 8) & 0xff) + Math.round(255 * pct);
  let b = (num & 0xff) + Math.round(255 * pct);
  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
};
const rgba = (hex, a) => {
  const c = hex.replace("#", "");
  const num = parseInt(c, 16);
  return `rgba(${num >> 16}, ${(num >> 8) & 0xff}, ${num & 0xff}, ${a})`;
};

export const applyThemeVars = (t) => {
  const root = document.documentElement;
  root.style.setProperty("--qq-header", t.header_color);
  root.style.setProperty("--qq-header-text", t.header_text_color);
  root.style.setProperty("--qq-accent", t.accent_color);
  root.style.setProperty("--qq-accent-hover", shade(t.accent_color, -0.08));
  root.style.setProperty("--qq-accent-50", rgba(t.accent_color, 0.09));
  root.style.setProperty("--qq-accent-100", rgba(t.accent_color, 0.18));
  root.style.setProperty("--qq-sidebar", t.sidebar_color);
  root.style.setProperty("--qq-sidebar-text", t.sidebar_color === "#FFFFFF" ? "#0F172A" : "#F8FAFC");
  root.style.setProperty("--qq-sidebar-muted", t.sidebar_color === "#FFFFFF" ? "#64748B" : "#94A3B8");
  root.style.setProperty("--qq-sidebar-hover", t.sidebar_color === "#FFFFFF" ? "#F1F5F9" : rgba("#FFFFFF", 0.08));
  root.setAttribute("data-theme", t.theme_preset || "custom");
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(defaultTheme);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/business");
      const t = { ...defaultTheme, ...data };
      setThemeState(t);
      applyThemeVars(t);
    } catch { /* not logged in yet */ }
    finally { setLoaded(true); }
  }, []);

  useEffect(() => { load(); applyThemeVars(defaultTheme); }, [load]);

  const applyPreview = (patch) => {
    const next = { ...theme, ...patch };
    setThemeState(next);
    applyThemeVars(next);
  };

  const setTheme = async (patch) => {
    const next = { ...theme, ...patch };
    setThemeState(next);
    applyThemeVars(next);
    const { data } = await api.put("/business", next);
    const merged = { ...defaultTheme, ...data };
    setThemeState(merged);
    applyThemeVars(merged);
    return merged;
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, applyPreview, reload: load, loaded }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
